import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { LobbyTypeEnum, MarketStatus } from '@sportspredict/shared';
import { ROUTES, TABS } from '@/constants';
import { Trade } from '@/api';
import { getGlobalTradeCount } from '@/utils';
import {
	setHasSeenInfo,
	setHasNotSeenInfo,
	setLastKnownRank,
} from '@/store/slices/lobby-details.slice.js';

interface UseLobbyDetailsEffectsProps {
	data: any;
	searchParams: URLSearchParams;
	setSearchParams: (params: URLSearchParams, opts?: any) => void;
	eventId: string | undefined;
	lobbyId: string | undefined;
	user: any;
	setHasEverTraded: (value: boolean) => void;
	hasEverTraded: boolean | null;
	hasEverTradedRef: { current: boolean | null };
	setIsOddsLocked: (value: boolean) => void;
	lobbyResponse: any;
	eventResponse: any;
	hasSeenInfo: boolean | undefined;
	isInitialized: boolean;
	showPasswordDialog: boolean;
	showSuccessDialog: boolean;
	setShowLobbyInfo: (value: boolean) => void;
	setFilterMatchIds: (value: any) => void;
	activeTab: string;
	userLeaderboardRank: any;
	isAdmin: boolean;
	isCrowdLockMode: boolean;
	isCrowdLockStarted: boolean;
	isCrowdLockEliminatedUser: boolean;
	shared: boolean;
	autoJoinPending: boolean;
	hasCrowdLockLobbyAccess: boolean;
	isMobile: boolean;
	isRegisteredUser: boolean;
	isSlatesLoadingData: boolean;
	setIsSlatesLoading: (value: boolean) => void;
	slates: any[];
	setSlatesData: (value: any[]) => void;
	setMatchClocks: (value: Map<string, any>) => void;
	currentMarketId: string | undefined;
	currentMarketStatus: string | undefined;
	lastSettledMarketRef: { current: string | null };
	setUserTrades: (value: any[]) => void;
}

export function useLobbyDetailsEffects({
	data,
	searchParams,
	setSearchParams,
	eventId,
	lobbyId,
	user,
	setHasEverTraded,
	hasEverTraded,
	hasEverTradedRef,
	setIsOddsLocked,
	lobbyResponse,
	eventResponse,
	hasSeenInfo,
	isInitialized,
	showPasswordDialog,
	showSuccessDialog,
	setShowLobbyInfo,
	setFilterMatchIds,
	activeTab,
	userLeaderboardRank,
	isAdmin,
	isCrowdLockMode,
	isCrowdLockStarted,
	isCrowdLockEliminatedUser,
	shared,
	autoJoinPending,
	hasCrowdLockLobbyAccess,
	isMobile,
	isRegisteredUser,
	isSlatesLoadingData,
	setIsSlatesLoading,
	slates,
	setSlatesData,
	setMatchClocks,
	currentMarketId,
	currentMarketStatus,
	lastSettledMarketRef,
	setUserTrades,
}: UseLobbyDetailsEffectsProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch<any>();

	useEffect(() => {
		if (!data.lobbyResponse) return;

		const isCrowdLock = data.lobbyResponse.type === LobbyTypeEnum.CrowdLock;
		const current = searchParams.get('isCrowdLock') === 'true';

		if (isCrowdLock && !current) {
			const next = new URLSearchParams(searchParams);
			next.set('isCrowdLock', 'true');
			setSearchParams(next, { replace: true });
		}

		if (!isCrowdLock && current) {
			const next = new URLSearchParams(searchParams);
			next.delete('isCrowdLock');
			setSearchParams(next, { replace: true });
		}
	}, [data.lobbyResponse?.type, searchParams, setSearchParams]);

	useEffect(() => {
		if (data.lobbyResponse?.type === LobbyTypeEnum.HeadToHead) {
			const query = location.search;
			navigate(`${ROUTES.H2H}/${eventId}/${lobbyId}${query}`, { replace: true });
		}
	}, [data.lobbyResponse, eventId, lobbyId, navigate, location.search]);

	useEffect(() => {
		if (!user) return;
		setHasEverTraded(getGlobalTradeCount(user) > 0);
	}, [user]);

	useEffect(() => {
		hasEverTradedRef.current = hasEverTraded;
	}, [hasEverTraded]);

	useEffect(() => {
		if (!data.eventResponse) {
			return;
		}

		setIsOddsLocked(data.eventResponse?.is_odds_locked);
	}, [data.eventResponse]);

	useEffect(() => {
		if (!lobbyId) return;
		if (!isInitialized) return;

		if (showPasswordDialog || showSuccessDialog) {
			setShowLobbyInfo(false);

			dispatch(setHasNotSeenInfo({ lobbyId } as any));
			return;
		}

		if (hasSeenInfo === false) {
			setShowLobbyInfo(true);

			dispatch(setHasSeenInfo({ lobbyId } as any));
		}
	}, [lobbyId, hasSeenInfo, isInitialized, showPasswordDialog, showSuccessDialog, dispatch]);

	useEffect(() => {
		setFilterMatchIds(null);
	}, [eventId, lobbyId]);

	useEffect(() => {
		if (activeTab === TABS.LEADERBOARD && userLeaderboardRank?.rank) {
			if (userLeaderboardRank?.rank > 0) {
				dispatch(setLastKnownRank({ lobbyId, rank: userLeaderboardRank?.rank } as any));
			}
		}
	}, [activeTab, userLeaderboardRank?.rank, lobbyId, dispatch]);

	useEffect(() => {
		if (isAdmin) return;

		const hasRequiredData = !!lobbyResponse && !!eventResponse;

		if (!hasRequiredData) return;

		if (isCrowdLockMode && hasCrowdLockLobbyAccess && isRegisteredUser) return;
		if (isCrowdLockMode && isCrowdLockEliminatedUser && isRegisteredUser) return;

		// Don't redirect during revival process
		if (lobbyResponse.is_revival_initiated && hasCrowdLockLobbyAccess && isRegisteredUser) return;

		const isCrowdLockClosedForSharedUser =
			shared && isCrowdLockMode && isCrowdLockStarted && !hasCrowdLockLobbyAccess;

		const shouldRedirect =
			((!shared || isCrowdLockClosedForSharedUser) &&
				!autoJoinPending &&
				!!lobbyResponse &&
				!!eventResponse &&
				!hasCrowdLockLobbyAccess) ||
			(!isRegisteredUser && lobbyResponse.is_private) ||
			(!isRegisteredUser && isCrowdLockMode);

		if (shouldRedirect) {
			const timer = setTimeout(() => {
				if (isMobile || isCrowdLockMode || !isRegisteredUser) {
					navigate(`${ROUTES.EVENT_DETAILS}?id=${eventId}`);
				} else {
					const backPath = location.state?.isFromHomePage ? ROUTES.ROOT : ROUTES.EVENTS;

					navigate(backPath, {
						state: { openPanelForEventId: eventId },
					});
				}
			}, 500);

			return () => clearTimeout(timer);
		}

		return undefined;
	}, [
		isAdmin,
		isCrowdLockMode,
		isCrowdLockStarted,
		isCrowdLockEliminatedUser,
		shared,
		autoJoinPending,
		lobbyResponse,
		eventResponse,
		hasCrowdLockLobbyAccess,
		isMobile,
		navigate,
		eventId,
		isRegisteredUser,
		location.state,
	]);

	useEffect(() => {
		setIsSlatesLoading(isSlatesLoadingData);
	}, [isSlatesLoadingData]);

	useEffect(() => {
		if (Array.isArray(slates)) {
			setSlatesData(slates);
		}
	}, [slates]);

	useEffect(() => {
		if (slates?.length) {
			const initialClocks = new Map();
			for (const slate of slates) {
				for (const match of slate.matches || []) {
					if (match.result && match.id) {
						initialClocks.set(match.id, match.result);
					}
				}
			}
			if (initialClocks.size > 0) {
				setMatchClocks(initialClocks);
			}
		}
	}, [slates]);

	useEffect(() => {
		if (
			!currentMarketId ||
			currentMarketStatus !== MarketStatus.Settled ||
			!user?.id ||
			lastSettledMarketRef.current === currentMarketId
		) {
			return;
		}

		lastSettledMarketRef.current = currentMarketId;

		(async () => {
			try {
				const updatedTrades = await Trade.filter({
					user_id: user.id,
					event_id: eventId,
				});

				const currentLobbyTrades = [];

				for (const t of updatedTrades ?? []) {
					if (String(t.lobby_id) === String(lobbyId)) {
						currentLobbyTrades.push(t);
					}
				}

				setUserTrades(currentLobbyTrades);
			} catch (err) {
				console.error('[refetch user trades on settle]', err);
			}
		})();
	}, [currentMarketId, currentMarketStatus, user?.id, eventId, lobbyId]);
}
